import React, { useEffect, useRef, useState } from 'react';
import { PageFlip } from 'page-flip';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

export const JournalFlipbook = ({ pages, keyParam }) => {
  const ref = useRef(null);
  const flip = useRef(null);
  const [page, setPage] = useState(0);
  const urls = pages.map((p) => `${BASE}${p}${keyParam ? `?key=${encodeURIComponent(keyParam)}` : ''}`);

  useEffect(() => {
    if (!ref.current || !urls.length) return undefined;
    const host = document.createElement('div');
    ref.current.appendChild(host);
    const nodes = urls.map((u, i) => {
      const pg = document.createElement('div');
      pg.className = 'bg-white';
      if (i === 0 || i === urls.length - 1) pg.dataset.density = 'hard';
      const img = document.createElement('img');
      img.src = u;
      img.alt = `Journal page ${i + 1}`;
      img.loading = i < 4 ? 'eager' : 'lazy';
      img.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';
      pg.appendChild(img);
      host.appendChild(pg);
      return pg;
    });
    const pf = new PageFlip(host, {
      width: 612, height: 792, size: 'stretch', minWidth: 280, maxWidth: 900, minHeight: 360, maxHeight: 1165,
      showCover: true, maxShadowOpacity: 0.5, mobileScrollSupport: true, usePortrait: true,
    });
    pf.loadFromHTML(nodes);
    pf.on('flip', (e) => setPage(e.data));
    flip.current = pf;
    const node = ref.current;
    return () => { pf.destroy(); flip.current = null; node.innerHTML = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.join('|')]);

  const btn = 'p-3 rounded-full border border-gold/50 text-gold hover:bg-gold/15 transition-colors disabled:opacity-30';
  return (
    <div data-testid="journal-flipbook">
      <div className="mx-auto max-w-5xl"><div ref={ref} /></div>
      <div className="mt-6 flex items-center justify-center gap-4">
        <button type="button" className={btn} onClick={() => flip.current?.flipPrev()} disabled={page === 0} aria-label="Previous page" data-testid="flipbook-prev"><ChevronLeft size={20} /></button>
        <span className="font-heading tracking-widest text-xs text-cbtu-cream min-w-[130px] text-center" data-testid="flipbook-page-indicator">PAGE {page + 1} OF {urls.length}</span>
        <button type="button" className={btn} onClick={() => flip.current?.flipNext()} disabled={page >= urls.length - 1} aria-label="Next page" data-testid="flipbook-next"><ChevronRight size={20} /></button>
        <a href={urls[page]} target="_blank" rel="noreferrer" className={btn} aria-label="Open this page full size" data-testid="flipbook-open-page"><Maximize2 size={18} /></a>
      </div>
      <p className="text-center text-cbtu-muted text-xs mt-3">Click or drag a page corner to turn the page. On phones, swipe.</p>
    </div>
  );
};

const pad = (n) => String(n).padStart(2, '0');

export const JournalCountdown = ({ releaseAt, onRelease }) => {
  const [now, setNow] = useState(Date.now());
  const target = new Date(releaseAt).getTime();
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (now >= target) onRelease(); }, [now, target, onRelease]);
  const s = Math.max(0, Math.floor((target - now) / 1000));
  const parts = [['DAYS', Math.floor(s / 86400)], ['HOURS', Math.floor((s % 86400) / 3600)], ['MIN', Math.floor((s % 3600) / 60)], ['SEC', s % 60]];
  return (
    <div className="flex gap-3" data-testid="journal-countdown">
      {parts.map(([l, v]) => (
        <div key={l} className="bg-cbtu-black border border-gold/40 rounded-sm px-4 py-3 text-center min-w-[72px]">
          <div className="font-display text-3xl gold-text-fill">{pad(v)}</div>
          <div className="font-heading text-[10px] tracking-widest text-cbtu-muted">{l}</div>
        </div>
      ))}
    </div>
  );
};
